import { useEffect, useState } from "react";
import client from "../lib/api";
import HeroBanner from "../components/HeroBanner";
import StoriesBar from "../components/StoriesBar";
import VideoRow from "../components/VideoRow";
import MusicRow from "../components/MusicRow";
import ShortsReel from "../components/ShortsReel";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { Sparkles, Upload } from "lucide-react";

export default function Home() {
  const { admin } = useAuth();
  const [data, setData] = useState({
    featured: [],
    stories: [],
    long: [],
    shorts: [],
    music: [],
    categories: [],
    byCat: {},
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [featured, stories, all, cats] = await Promise.all([
          client.get("/videos/featured"),
          client.get("/videos/stories"),
          client.get("/videos"),
          client.get("/videos/categories"),
        ]);
        const allVids = all.data;
        const long = allVids.filter((v) => v.type === "long");
        const shorts = allVids.filter((v) => v.type === "short");
        const music = allVids.filter((v) => v.type === "music");
        const byCat = {};
        for (const c of cats.data) {
          byCat[c] = allVids.filter((v) => v.category === c && v.type !== "story" && v.type !== "music");
        }
        setData({
          featured: featured.data,
          stories: stories.data,
          long, shorts, music,
          categories: cats.data,
          byCat,
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const heroVideo = data.featured[0] || data.long[0] || data.shorts[0] || null;
  const empty = !loading && data.long.length === 0 && data.shorts.length === 0 && data.stories.length === 0 && (data.music?.length || 0) === 0;

  return (
    <div data-testid="home-page">
      <HeroBanner video={heroVideo} />

      <StoriesBar stories={data.stories} isAdmin={!!admin} />

      {empty ? (
        <div className="px-4 sm:px-8 lg:px-20 py-20 text-center">
          <div className="max-w-2xl mx-auto glass rounded-2xl p-10 fade-up">
            <Sparkles className="w-10 h-10 text-[#FF6B00] mx-auto" />
            <h2 className="mt-4 text-2xl sm:text-3xl font-bold text-white">Your Nexus is silent.</h2>
            <p className="mt-3 text-zinc-400">No videos have been uploaded yet. Sign in as admin to upload your first transmission — long videos, vertical shorts, or 24-hour stories.</p>
            {admin ? (
              <Link to="/admin" data-testid="empty-state-studio-btn" className="aura-button mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-full text-sm uppercase tracking-wider">
                <Upload className="w-4 h-4" /> Open Studio
              </Link>
            ) : (
              <Link to="/admin/login" data-testid="empty-state-login-btn" className="aura-button mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-full text-sm uppercase tracking-wider">
                Admin Login
              </Link>
            )}
          </div>
        </div>
      ) : (
        <>
          {data.shorts.length > 0 && <ShortsReel shorts={data.shorts} />}
          {data.music?.length > 0 && (
            <MusicRow accent="Audio" title="Music Drops" tracks={data.music} testid="row-music" />
          )}
          {data.featured.length > 0 && (
            <VideoRow accent="Spotlight" title="Featured Drops" videos={data.featured} testid="row-featured" />
          )}
          {data.long.length > 0 && (
            <VideoRow accent="Latest" title="Fresh Transmissions" videos={data.long} testid="row-latest" />
          )}
          {data.categories.map((c) => (
            data.byCat[c]?.length > 0 ? (
              <VideoRow
                key={c}
                accent={c}
                title={c}
                videos={data.byCat[c]}
                testid={`row-cat-${c}`}
              />
            ) : null
          ))}
        </>
      )}

      <LandingsFooter />

      <footer className="mt-16 px-4 sm:px-8 lg:px-20 py-10 border-t border-white/5 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-zinc-600">TheSaiyanVictoria · Nexus · Power Level Unlimited</p>
      </footer>
    </div>
  );
}

/* Landing pages list — fetched client-side. Primary purpose: route logged-in
 * users to Vanessa's SEO landing pages. SEO authority flow for crawlers is
 * handled by /api/sitemap.xml + /api/discover + the cross-link footer on every
 * /v/<slug> SSR page. */
function LandingsFooter() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await client.get("/landing/index.json", { params: { limit: 60 } });
        if (cancelled) return;
        const list = r.data?.items;
        if (Array.isArray(list)) setItems(list);
      } catch {
        /* silent — section just doesn't render */
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!items.length) return null;
  return (
    <section data-testid="home-landings-footer" className="mt-12 px-4 sm:px-8 lg:px-20">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs uppercase tracking-[0.3em] text-[#FFB800] font-black">More from TheSaiyanVictoria</h2>
        <a href="/discover" data-testid="home-discover-link" className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF6B00]">
          Discover all ↗
        </a>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.slice(0, 24).map((p) => (
          <li key={p.slug}>
            <a
              href={`/v/${p.slug}`}
              className="block px-3 py-2 rounded-lg bg-zinc-950/40 border border-white/5 text-zinc-300 text-xs hover:bg-[#FF6B00]/10 hover:border-[#FF6B00]/30 hover:text-[#FFB800] transition-colors"
            >
              {p.title || p.slug}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
